const en = {
  translation: {
    // ─── Common ────────────────────────────────────────────────────────
    common: {
      loading: 'Loading...',
      error: 'Error',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      delete: 'Delete',
      done: 'Done',
      back: 'Back',
      close: 'Close',
      retry: 'Retry',
      ok: 'OK',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      and: 'and',
      years: 'yrs',
      noData: 'No data',
      seeAll: 'See All',
      logout: 'Logout',
      logoutConfirm: 'Are you sure you want to logout?',
      continue: 'Continue',
      month: 'Month',
      months: 'Months',
    },

    // ─── Tabs ──────────────────────────────────────────────────────────
    tabs: {
      home: 'HOME',
      classes: 'CLASSES',
      training: 'TRAINING',
      profile: 'PROFILE',
    },

    // ─── Auth ──────────────────────────────────────────────────────────
    auth: {
      welcomeBack: 'Welcome Back',
      signInSubtitle: 'Sign in to your account',
      appDescription: 'Manage your CBJJ Saigon membership, classes, and training progress.',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign In',
      noAccount: "Don't have an account?",
      register: 'Register',
      createAccount: 'Create Account',
      joinCbjj: 'Join CBJJ Saigon',
      firstName: 'First Name',
      lastName: 'Last Name',
      iAmA: 'I am a:',
      alreadyHaveAccount: 'Already have an account?',
      signInLink: 'Sign In',
      // Roles
      roleAthlete: 'Athlete',
      roleGuardian: 'Guardian',
      roleCoach: 'Coach',
      // Validation
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      invalidNumber: 'Please enter a valid number',
      loginFailed: 'Login failed',
      registrationFailed: 'Registration failed',
      // Forgot / Reset password
      forgotPassword: 'Forgot Password?',
      forgotPasswordSubtitle: 'Enter your email and we\'ll send you a reset link',
      sendResetLink: 'Send Reset Link',
      resetEmailSent: 'Check Your Email',
      resetEmailSentDetail: 'We\'ve sent a password reset link to your email address.',
      backToSignIn: 'Back to Sign In',
      resetPassword: 'Reset Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      passwordMinLength: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
      passwordResetSuccess: 'Your password has been reset successfully!',
      // Email verification
      verifyEmail: 'Verify Your Email',
      verifyEmailSubtitle: 'We sent a verification code to {{email}}',
      verificationEmailSent: 'Verification email sent!',
      verificationCode: 'Verification Code',
      verifyCode: 'Verify Code',
      verificationCodeRequired: 'Please enter the verification code',
      invalidVerificationCode: 'Invalid verification code. Please try again.',
      tooManyRequests: 'Too many attempts. Please try again later.',
      verificationSendFailed: 'Failed to send verification email',
      syncFailed: 'Failed to sync account. Please try again.',
      goToVerification: 'Verify Email',
      continueToApp: 'Continue',
      resendVerification: 'Resend Verification Email',
      resendIn: 'Resend in {{seconds}}s',
      // Social login
      or: 'or',
      continueWithGoogle: 'Continue with Google',
      continueWithApple: 'Continue with Apple',
      // Auth callback
      authenticating: 'Authenticating...',
      authenticationFailed: 'Authentication Failed',
      callbackError: 'Something went wrong during authentication. Please try again.',
      callbackNoVerifier: 'No authentication data received. Please try signing in again.',
      // Profile completion (new users / OAuth)
      completeYourProfile: 'Complete Your Profile',
      completeProfileSubtitle: 'Just a few more details to get you started.',
      dateOfBirth: 'Date of Birth',
      birthdayRequired: 'Date of birth is required',
      dateOfBirthRequired: 'Date of birth is required',
      birthdayFormat: 'Use format YYYY-MM-DD',
      birthdayInvalid: 'Please enter a valid date',
      selectDate: 'Select date',
      profileUpdateFailed: 'Failed to update profile',
      // Terms
      termsPrefix: 'I agree to the',
      privacyPolicy: 'Privacy Policy',
      termsAnd: 'and',
      termsOfService: 'Terms of Service',
      termsRequired: 'You must accept the Privacy Policy and Terms of Service',
      ageRequirement: 'You must be at least 18 years old to register as a main user',
    },

    // ─── Home Screen ───────────────────────────────────────────────────
    home: {
      checkIn: 'Check-in',
      scanAtDesk: 'Scan at the front desk',
      idPrefix: 'ID',
      nextClass: 'UPCOMING CLASSES',
      today: 'TODAY',
      coachTba: 'Coach TBA',
      coachPrefix: 'Coach {{name}}',
      mainMat: 'Main Mat',
      noClassesToday: 'No upcoming classes today',
      pendingActivation: {
        title: 'Activation Pending',
        body: 'Your payment is being verified by our team.\n\nIt can take up to 1 business day to verify payment.\n\nQR check-in will be available once activated.',
      },
      membershipPaused: {
        title: 'Membership Paused',
        body: 'Your membership is currently paused.\n\nCheck-in is not available during the pause period.',
      },
      timeRestriction: 'Plan limited to {{start}} - {{end}}',
      noClassesInAllowedHours: 'No classes available in your plan\'s allowed hours',
      dayRestrictionToday: 'Your plan does not allow classes today',
      planNotValidToday: 'Your membership plan is not valid for classes today',
    },

    // ─── Classes Screen ────────────────────────────────────────────────
    classes: {
      timetable: 'Time-table',
      loadingClasses: 'Loading classes...',
      noClassesScheduled: 'No classes scheduled',
      dayClosed: 'Closed today',
      dayClosedOther: 'Closed',
      dayClosedPublicHoliday: 'Closed today for Public Holiday',
      closedPublicHoliday: 'Closed for Public Holiday',
      closedBadge: 'Closed',
      publicHolidayBadge: 'Public Holiday',
      timeChanged: 'Time changed',
      timeRestriction: 'Your plan allows classes {{start}} - {{end}}',
      outsidePlanHours: 'Outside plan hours',
      dayNotAllowed: 'Your membership plan does not allow classes on this day',
      // Day abbreviations
      dayMon: 'MON',
      dayTue: 'TUE',
      dayWed: 'WED',
      dayThu: 'THU',
      dayFri: 'FRI',
      daySat: 'SAT',
      daySun: 'SUN',
    },

    // ─── Training Screen ───────────────────────────────────────────────
    training: {
      title: 'Training Tracking',
      currentRank: 'CURRENT RANK',
      stripe_one: '{{count}} Stripe',
      stripe_other: '{{count}} Stripes',
      timeInBelt: 'Time in Belt',
      months: '{{count}} Months',
      days: '{{count}} Days',
      lastPromo: 'Last Promo',
      coachNotes: 'COACH NOTES',
      setRank: {
        title: 'Set your current rank',
        subtitle: 'Tell us your belt and stripes so your progression starts from the right place. Your coach records all future promotions.',
        belt: 'Belt',
        stripes: 'Stripes',
        promoDate: 'When were you last promoted? (optional)',
        submit: 'Save my rank',
        note: 'This can only be set once — after this, promotions come from your coach.',
        beltRequired: 'Please select your belt',
        alreadySet: 'Your rank has already been recorded.',
      },
    },

    // ─── Profile Screen ────────────────────────────────────────────────
    profile: {
      guardian: 'Guardian: {{name}}',
      guardianTag: 'Guardian',
      age: 'Age: {{count}}',
      dob: 'Date of Birth',
      picture: {
        title: 'Profile Picture',
        upload: 'Upload New Photo',
        remove: 'Remove Photo',
      },
      membershipPayment: 'MEMBERSHIP & PAYMENT',
      currentPlan: 'Current Plan',
      noPlan: 'No Plan',
      active: 'ACTIVE',
      inactive: 'INACTIVE',
      nextPayment: 'Next Payment',
      manageMembership: 'Manage Membership',
      hideHistory: 'Hide History',
      paymentHistory: 'Payment History',
      payment: 'Payment',
      noPaymentHistory: 'No payment history',
      familyMembers: 'FAMILY MEMBERS',
      pendingActivation: {
        title: 'Activation Pending',
        body: 'Your payment is being verified by our team.\n\nIt can take up to 1 business day to verify payment.',
      },
      belt: '{{name}} Belt',
      viewing: 'Viewing',
      lightMode: 'Light Mode',
      darkMode: 'Dark Mode',
      language: 'Language',
      appSettings: 'App Info',
      personal: {
        edit: 'Edit personal details',
        title: 'Personal Details',
        firstName: 'First name',
        lastName: 'Last name',
        birthday: 'Date of birth',
        save: 'Save',
        nameRequired: 'First and last name are required',
      },
      signOut: 'Sign Out',
      signingOut: 'Signing out...',
      noFamilyMembers: 'No family members linked yet',
      addFamilyMember: 'Add Family Member',
      invite: {
        title: 'Invite to App',
        editTitle: 'Update Login Email',
        edit: 'Edit Invite',
        invite: 'Invite to App',
        registered: 'Registered',
        help: 'Share this QR code or link with {{name}} so they can register for their own account.',
        linkLabel: 'Invite Link',
        copy: 'Copy link',
        copied: 'Link copied to clipboard',
        expires: 'Expires on {{date}}',
        regenerate: 'Generate New Link',
        failed: 'Failed to generate invite',
        welcome: 'You\'re Invited!',
        invitedBy: 'Invited by {{guardian}}',
        athleteName: 'Name',
        invalid: 'Invalid Invite',
        notFound: 'This invite link is invalid or has expired.',
      },
      managedBy: 'Managed by {{name}}',
      managedByFamily: 'Managed by your family',
      managedByGuardian: 'This account is managed by {{guardian}}.',
      started: 'Started {{date}}',
      ends: 'Ends {{date}}',
      membership: {
        title: 'Membership',
        plan: 'Plan',
        status: 'Status',
        expires: 'Expires',
        renew: 'Renew Membership',
        noMembership: 'No active membership',
        upgrade: 'Upgrade Plan',
        downgrade: 'Downgrade Plan',
      },
      logout: 'Log Out',
      logoutConfirm: 'Are you sure you want to log out?',
      cancel: 'Cancel',
      confirm: 'Confirm',
      appName: 'App Name',
      version: 'Version',
      build: 'Build',
      platform: 'Platform',
      bundleId: 'Bundle ID',
      channel: 'Channel',
      updateId: 'Update ID',
      unknown: 'Unknown',
      pausedUntil: 'Paused until {{date}}',
      pausedIndefinite: 'Indefinite pause',
      bodyMeasurements: 'Measurements',
      bodyMeasurementsHint: 'Optional — used to determine the member\'s gi size',
      weight: 'Weight',
      height: 'Height',
      metric: 'Metric',
      imperial: 'Imperial',
    },

    // ─── Membership Paused Banner ──────────────────────────────────────
    pausedBanner: {
      title: 'Membership Paused',
      until: 'Until {{date}}',
      indefinite: 'Indefinite pause',
      reason: 'Reason: {{reason}}',
    },

    // ─── App Header ────────────────────────────────────────────────────
    header: {
      athlete: 'Athlete',
      switchRole: 'SWITCH ROLE',
      viewingAs: 'VIEWING AS',
      selfSuffix: '(Me)',
    },

    // ─── Billing Screen ────────────────────────────────────────────────
    billing: {
      membershipPricing: 'Membership & Pricing',
      checkout: 'Checkout',
      choosePlan: '1. Choose Plan',
      checkoutStep: '2. Checkout',
      heroTitle: 'Membership',
      heroPricing: '& Pricing.',
      heroSubtitle: 'World-class Jiu-Jitsu instruction. Choose the tier that fits your journey.',
      selectPlan: 'Select Plan',
      perMonth: '/ mo',
      currency: 'VND',
      targetedOfferBadge: 'Exclusive Offer',
      // Community Grant
      communityGrant: 'Community Grant',
      communityGrantIndex: '06',
      communityGrantPrice: 'Up to 45% Off',
      communityGrantNote: 'BY APPLICATION',
      communityGrantDesc: 'Supporting active local athletes, students, and youth. High-level training made accessible.',
      applyNow: 'Apply Now',
      communityGrantAlert: 'Redirecting to application form...',
      // Order Summary
      orderSummary: 'ORDER SUMMARY',
      monthlySubscription: 'Monthly Subscription',
      subtotal: 'Subtotal',
      platformFees: 'Platform Fees',
      calculatedNext: 'Calculated next',
      totalDueToday: 'Total Due Today',
      total: 'Total',
      // Payment Methods
      paymentMethod: 'Payment Method',
      localBank: 'Local Bank / VietQR',
      localBankFees: '0% Fees · Pay via App',
      internationalCard: 'International Card',
      internationalCardSub: 'Visa, MasterCard, Amex',
      savedCards: 'SAVED CARDS',
      recurringTokenized: 'Recurring · Tokenized',
      tokenBadge: 'Token',
      // OnePay (disabled)
      onepayPayment: 'OnePay Card Payment',
      onepayDisabled: 'Credit card payments coming soon',
      onepayCard: 'Pay with Card',
      payWithCard: 'Visa, MasterCard, JCB',
      useSavedCard: 'Use a saved card',
      // 9Pay Panel
      securedBy9Pay: 'Secured by 9Pay',
      scanViaBanking: 'Scan via Banking App',
      scanViaBankingDesc: 'Open your local banking app or e-wallet to scan this code. The exact amount is pre-filled.',
      noAutoDeduct: 'No Auto-Deduct.',
      noAutoDeductDesc: 'You will receive a new unique 9Pay link every billing cycle directly via Zalo & Email.',
      // Card Panel
      cardDetails: 'Card Details',
      savedCardTokenized: 'Saved card · Tokenized via OnePay',
      secureRedirect: 'Secure Redirect',
      secureRedirectDesc: 'You will be securely redirected to OnePay\'s hosted payment page to enter your card details. We never see your full card number.',
      saveCardLabel: 'Save card for next billing cycle',
      saveCardDesc: 'Your card is securely processed and tokenized via OnePay. We do not store your actual card numbers.',
      processing: 'Processing...',
      payAmount: 'Pay {{amount}}',
      // Footer
      vatCompliant: 'Centralized VAT & E-Invoice Compliant',
      // Errors
      errorOpenPayment: 'Unable to open payment page',
      paymentError: 'Payment Error',
      saveAmount: 'Save {{amount}} every {{count}} months',
      discountPercent: '{{percent}}% off',
      discountFreeMonths: '{{count}} month free',
      discountFreeMonths_other: '{{count}} months free',
    },

    // ─── Payment Result Screen ─────────────────────────────────────────
    paymentResult: {
      waitingTitle: 'Waiting for Payment',
      waitingSubtitle: 'Complete your payment on the OnePay page.\nThis screen will update automatically.',
      successTitle: 'Payment Successful',
      successSubtitle: 'Your membership has been activated.',
      cardSaved: 'Card saved for future billing',
      failedTitle: 'Payment Failed',
      failedSubtitle: 'The payment was not completed. Please try again.',
      timeoutTitle: 'Payment Timed Out',
      timeoutSubtitle: "We didn't receive confirmation from OnePay. If you completed the payment, it may still be processing.",
      plan: 'Plan',
      amount: 'Amount',
      status: 'Status',
      statusPending: 'Pending',
      statusCompleted: 'Completed',
      statusFailed: 'Failed',
      statusTimeout: 'Timeout',
      tryAgain: 'Try Again',
      backToHome: 'Back to Home',
      cancelGoBack: 'Cancel & Go Back',
    },

    // ─── VietQR Screen ────────────────────────────────────────────────
    vietqr: {
      awaitingTitle: 'Complete Your Payment',
      awaitingSubtitle: 'Scan the QR code with your banking app to pay. Once you finish, "I have completed the payment".',
      confirmedTitle: 'Payment Submitted',
      confirmedSubtitle: 'We received your confirmation.\n\nIt can take up to 1 business day for our team to verify the payment.\n\nYou will receive an email payment receipt once confirmed.',
      confirmedContinueNote: 'You can continue into the app while waiting for final payment confirmation.',
      successTitle: 'Payment Successful',
      successSubtitle: 'Your membership has been activated. Welcome to CBJJ!',
      failedTitle: 'Payment Failed',
      failedSubtitle: 'The payment was not completed. Please try again.',
      scanToPay: 'Scan to Pay',
      accountName: 'Account Name',
      accountNumber: 'Account Number',
      bankBin: 'Bank (BIN)',
      orderNumber: 'Order #',
      amount: 'Amount',
      orderDetails: 'Order details',
      membershipOrder: 'Membership order',
      subtotal: 'Subtotal',
      discounts: 'Discounts',
      total: 'Total',
      status: 'Status',
      statusPending: 'Pending',
      statusCompleted: 'Completed',
      statusFailed: 'Failed',
      statusAwaitingVerification: 'Awaiting Verification',
      tryAgain: 'Try Again',
      markPaidBtn: 'I have completed the payment',
      markingPaid: 'Confirming...',
      continueToApp: 'Continue to app',
      viewConfirmation: 'View Confirmation Status',
      backToPaymentInstructions: 'View QR Code & Bank Transfer Details',
      cancelPayment: 'Cancel Payment',
      cancelConfirmTitle: 'Cancel Payment?',
      cancelConfirmDesc: 'Are you sure you want to cancel this payment? This invoice and QR code will be permanently invalidated, and you will need to restart the checkout process.',
      cancelConfirmBtn: 'Yes, Cancel Payment',
      keepPaymentBtn: 'No, Keep Payment',
      saveQrCode: 'Save QR Code',
      securedByVietQR: 'Secured by VietQR',
      pushPaymentTitle: 'VietQR Push Payment',
      pushPaymentSubtitle: 'Scan the QR code with any Vietnamese banking app. The exact amount and order reference are pre-filled.',
      noAutoDeduct: 'No Auto-Deduct',
      noAutoDeductDesc: 'This is a manual push payment. You will initiate the transfer yourself from your banking app each billing cycle.',
      mediaPermissionRequired: 'Permission to access your photos is required to save the QR code.',
      qrSavedSuccess: 'QR code saved to your photo library!',
      qrSavedFailed: 'Failed to save the QR code. Please take a screenshot instead.',
    },

    // ─── Pending Verification Banner ───────────────────────────────────
    pendingBanner: {
      title: 'Payment awaiting verification',
      listening: 'Listening...',
      checkNow: 'Check now',
    },

    // ─── Activation Success Banner ──────────────────────────────────────
    activationSuccess: {
      title: 'Your account has been successfully activated!',
    },

    // ─── Subscription Screen (forced onboarding) ──────────────────────
    subscription: {
      choosePlan: 'Choose Your Plan',
      heroTitle: 'Choose Your Membership',
      heroSubtitle: 'Select a plan to get started with CBJJ training.',
      choosePlanSubtitle: 'Select the plan that fits your training goals. You can add family members at checkout.',
      duration: 'Billing Cycle',
      plans: 'Plans',
      vietqrPush: 'VietQR Push Payment',
      vietqrPushSub: '0% Fees · Pay via Banking App',
      addMembers: 'Add Members',
      addMembersTitle: 'Select Plans for Family',
      addMembersSubtitle: 'Add family members who need memberships. One payment covers everyone.',
      addFamilyMember: 'ADD FAMILY MEMBER',
      addMember: 'Add Member',
      addFamilyMembers: 'ADD FAMILY MEMBERS',
      add: 'Add',
      addFamilyHint: 'Tap + Add to add family members (children or dependents) and select plans for them.',
      familyMembers: 'Family Members',
      familyMembersSubtitle: 'Select membership plans for each family member.',
      selectPlansForMembers: 'SELECT PLANS FOR EACH MEMBER',
      selectMembershipToProceed: 'Select a membership for yourself or family members to proceed',
      selectPlan: 'Select Plan →',
      skipPlanTitle: 'Skip for Now',
      skipPlanDesc: 'Perfect if you just want to add your family members now. You can sign up yourself anytime from your profile.',
      continueToAddFamily: 'Continue to Add Family',
      addFamily: 'Add Family',
      addAnotherMember: 'Add Another',
      familyPromptTitle: 'Adding Family Members?',
      familyPromptSubtitle: 'Do you have children or dependents who need a membership? One payment covers everyone.',
      familyPromptYes: 'Yes, Add Family',
      familyPromptNo: 'No, Just Me',
      selectPlanForMember: 'Select a membership plan for {{name}}',
    },

    // ─── Referral System ─────────────────────────────────────────────
    referral: {
      title: 'Refer a Friend',
      subtitle: 'Refer friends, everyone gets free months',
      howItWorks: 'Share your code with friends. When they join and stay active for 6 months, they get +1 free month (guaranteed). You get +1 free month for each friend who is still active at 6 months.',
      yourCode: 'Your referral code',
      copy: 'Copy',
      copied: 'Copied!',
      share: 'Share',
      shareMessage: 'Join me at CBJJ Saigon! Use my referral code {{code}} and we both get a free month after 6 months. https://cbjj.app',
      refereeTitle: 'You Used a Referral Code',
      redemptionsTitle: 'Your Referrals',
      stats: {
        signups: 'Signups',
        rewards: 'Rewards',
        pending: 'Pending',
      },
      rewardStatus: {
        awarded: 'Rewarded',
        pending: 'Pending',
        skipped: 'Not Eligible',
      },
      redeemedOn: 'Joined {{date}}',
      rewardDue: 'Reward due {{date}}',
      status: {
        unused: 'Your code is ready — share it with friends!',
        pending: '{{name}} joined on {{date}}. You\'ll get +1 free month on {{rewardDate}} if your membership is still active.',
        rewardAwarded: '+1 free month added on {{date}}. Thanks for sharing!',
        rewardForfeited: 'Reward could not be applied — membership was no longer active.',
        refereePending: 'You used {{name}}\'s code on {{date}}. You\'ll get +1 free month on {{rewardDate}} if your membership is still active.',
        refereeAwarded: 'You received +1 free month from {{name}} on {{date}}.',
        refereeForfeited: 'Your referral reward could not be applied — membership was no longer active.',
        codeRevoked: 'This referral code has been revoked and can no longer be used.',
      },
      checkoutPrompt: 'Have a referral code?',
      apply: 'Apply',
      applied: 'Referral applied',
      remove: 'Remove',
      rewardChip: '+1 free month at month 6',
      appliedSuccess: '✓ Referral applied. You and {{name}} will each get +1 free month after 6 months of active membership.',
      invalidCode: 'Invalid referral code',
      unappliedCodeTitle: 'Apply Referral Code?',
      unappliedCodeMessage: 'You entered a referral code but haven\'t applied it yet. Tap "Apply" to use it, or clear the field to continue without it.',
      membershipNote: {
        pending: 'Referral bonus pending — +1 free month will be applied on {{date}}',
        awarded: 'Referral bonus applied — +1 free month added on {{date}}',
        forfeited: 'Referral bonus could not be applied',
      },
    },

    // ─── Pickup Persons ───────────────────────────────────────────────
    pickup: {
      title: 'Pickup & Emergency Contacts',
      sectionLabel: 'PICKUP & EMERGENCY',
      subtitle: 'People authorized to pick up your children, and who to call in an emergency.',
      manage: 'Authorized pickup persons ({{count}})',
      empty: 'No pickup persons yet. Add someone who can collect your child.',
      add: 'Add pickup person',
      addPerson: 'Add Pickup Person',
      editPerson: 'Edit Pickup Person',
      name: 'Full name',
      phone: 'Phone number',
      relationship: 'Relationship (e.g. Aunt)',
      relationshipPlaceholder: 'e.g. Aunt, Uncle, Grandparent',
      emergencyName: 'Emergency contact name',
      emergencyPhone: 'Emergency contact phone',
      emergency: 'Emergency',
      addPhoto: 'Add photo',
      delete: 'Remove',
      deleteConfirm: 'Remove this pickup person?',
      deleteConfirmMessage: 'This action cannot be undone. Are you sure you want to remove this pickup person?',
      photoUploadError: 'Failed to upload photo',
      requiredFields: 'Name and phone are required',
      nameRequired: 'Full name is required',
      phoneRequired: 'Phone number is required',
      fieldRequired: 'This field is required',
      photoRequired: 'A photo is required',
      accessDenied: 'Access Denied',
      promptTitle: 'Who can pick up your kids?',
      promptBody: 'Set up authorized pickup persons and emergency contacts so our staff knows who can collect your child.',
      promptCta: 'Set up now',
      promptLater: 'Maybe later',
    },

    // ─── Backend error keys (returned as detail in HTTP errors) ───────
    errors: {
      not_authenticated: 'Not authenticated',
      invalid_token_payload: 'Invalid token payload',
      invalid_or_expired_token: 'Invalid or expired token',
      invalid_target_scheme: 'Invalid target URL scheme for auth callback proxy',
      user_not_found_or_inactive: 'User not found or inactive',
      insufficient_permissions: 'Insufficient permissions',
      role_not_authorized: 'Role not authorized',
      not_authorized_athlete: 'Not authorized to access this athlete',
      class_not_found: 'Class not found',
      location_not_found: 'Location not found',
      assignment_not_found: 'Assignment not found',
      assignment_already_exists: 'Assignment already exists',
      family_link_already_exists: 'Family link already exists',
      not_authorized: 'Not authorized',
      link_not_found: 'Link not found',
      user_not_found: 'User not found',
      email_already_registered: 'Email already registered',
      email_already_in_use: 'This email is already in use by another account',
      sub_user_already_active: 'This family member has already activated their account',
      invalid_credentials: 'Invalid credentials',
      invalid_qr_payload: 'Invalid QR payload',
      qr_code_already_used: 'QR code already used',
      card_token_not_found: 'Card token not found',
      card_token_not_found_or_inactive: 'Card token not found or inactive',
      membership_not_found: 'Membership not found',
      missing_transaction_ref: 'Missing transaction reference',
      invalid_secure_hash: 'Invalid secure hash — possible tampering',
      payment_record_not_found: 'Payment record not found',
      no_bank_account_configured: 'No bank account configured for VietQR payments',
      not_a_vietqr_payment: 'This is not a VietQR payment',
      payment_already_completed: 'Payment already completed',
      payment_not_pending: 'Payment is not in pending status',
      payment_expired: 'Payment has expired',
      admin_only: 'This action is restricted to administrators only',
      internal_server_error: 'Internal server error',
      auto_logged_qr: 'Auto-logged from QR check-in',
      kids_plan_age_restriction: 'Kids plans are only available for ages 5-15',
      adult_plan_age_restriction: 'Adult plans are not available for ages 5-15',
      cannot_delete_plan_with_subscribers: 'Cannot delete plan with existing members. Deactivate instead.',
      cannot_delete_plan_with_members: 'Cannot delete plan with existing members. Deactivate instead.',
      cannot_subscribe_to_archived_plan: 'Cannot subscribe to an inactive plan.',
      cannot_subscribe_to_inactive_plan: 'Cannot subscribe to an inactive plan.',
      membership_plan_not_found: 'Membership plan not found',
      cannot_delete_last_active_bank_account: 'Cannot delete the last active bank account. Please create another active account first.',
      cannot_pause_inactive_membership: 'Can only pause active memberships',
      membership_already_paused: 'Membership is already paused',
      pause_start_date_in_past: 'Pause start date cannot be in the past',
      pause_end_date_before_start: 'Pause end date must be after start date',
      pause_end_date_exceeds_membership: 'Pause end date cannot exceed membership end date',
      membership_not_paused: 'Membership is not paused',
      no_active_pause_found: 'No active pause found',
      membership_time_restricted: 'Check-in not allowed: your membership plan is limited to specific hours',
      membership_day_restricted: 'Check-in not allowed: your membership plan is not valid for classes today',
      admin_user_not_found: 'Admin user not found',
      pickup_person_not_found: 'Pickup person not found',
      field_required: 'This field is required',
      referral: {
        code_not_found: 'Referral code not found',
        already_used: 'This referral code has already been used',
        already_redeemed: 'You have already used this referral code',
        self_redeem: 'You cannot use your own referral code',
        same_family: 'You cannot use a referral code from someone in your family',
        not_eligible_existing_member: 'Only new members can use a referral code',
        code_revoked: 'This referral code has been revoked and is no longer valid',
        referrer_not_active: 'This referral code is from an inactive member. Only active members can refer new users.',
        generation_failed: 'Failed to generate referral code. Please try again.',
        unknown: 'An error occurred with the referral code',
      },
      legal_name_mismatch: 'The name you entered does not match your account name.',
      legal_scroll_required: 'You must scroll to the end of the document.',
      legal_affirmations_required: 'You must accept all required affirmations.',
      legal_sub_user_cannot_sign: 'Managed accounts cannot sign legal documents directly.',
      legal_doc_not_found: 'Document not found.',
      guardian_waiver_required: 'Guardian waiver required for account access.',
    },

    // ─── Belt names ────────────────────────────────────────────────────
    belts: {
      white: 'White',
      grey: 'Grey',
      yellow: 'Yellow',
      orange: 'Orange',
      green: 'Green',
      blue: 'Blue',
      purple: 'Purple',
      brown: 'Brown',
      black: 'Black',
    },

    // ─── Skill Requirements (i18n-only, no DB) ────────────────────────
    skillRequirements: {
      white: {
        shrimp_hip_escape: {
          name: 'Shrimp/Hip Escape',
          description: 'Basic hip movement and escape technique',
        },
        bridge_roll_escape: {
          name: 'Bridge and Roll Escape',
          description: 'Escape from mount position',
        },
        closed_guard_maintenance: {
          name: 'Closed Guard Maintenance',
          description: 'Maintain closed guard position',
        },
        armbar_from_guard: {
          name: 'Armbar from Guard',
          description: 'Basic submission from closed guard',
        },
        rear_naked_choke: {
          name: 'Rear Naked Choke',
          description: 'Basic choke from back control',
        },
      },
      grey: {
        guard_passing_basics: {
          name: 'Guard Passing Basics',
          description: 'Basic guard passing techniques',
        },
        triangle_choke_setup: {
          name: 'Triangle Choke Setup',
          description: 'Triangle choke from guard',
        },
        kimura_from_guard: {
          name: 'Kimura from Guard',
          description: 'Kimura lock from closed guard',
        },
        side_control_escapes: {
          name: 'Side Control Escapes',
          description: 'Escape from side control',
        },
        basic_takedowns: {
          name: 'Basic Takedowns',
          description: 'Single leg and double leg takedowns',
        },
      },
      yellow: {},
      orange: {},
      green: {},
      blue: {},
      purple: {},
      brown: {},
      black: {},
    },

    // ─── Membership Management Screen ────────────────────────────────────
    mgmt: {
      title: 'Manage Memberships',
      membershipsTab: 'Memberships',
      addMemberTab: 'Add Member',
      paymentsTab: 'Payments',
      started: 'Started {{date}}',
      startDate: 'Start Date',
      ended: 'Ended {{date}}',
      ends: 'Ends {{date}}',
      renew: 'Renew',
      renewEarly: 'Renew early',
      addToPayment: 'Add to payment',
      queuedForPayment: 'Added to payment',
      renewalsSection: 'Renewals',
      totalEstimateHint: 'Final amount is confirmed on the payment screen',
      renewalsDropped: 'Some renewals were removed because they are no longer valid. Please check and try again.',
      payNow: 'Pay Now',
      addPlan: 'Add Plan',
      selectPlan: 'Select Plan',
      selectNewPlan: 'Select New Plan',
      addMember: 'Add Member',
      addMemberStep1: 'Enter Member Details',
      addMemberPromptTitle: 'Add a Family Member',
      addMemberPromptDesc: 'Add a child or dependent and choose a plan for them.',
      addAndPay: 'Add & Pay',
      noMemberships: 'No Memberships Yet',
      noMembershipsDesc: 'Add a family member or select a plan to get started.',

      // Change / cancel plan
      changePlan: 'Change Plan',
      cancelMembership: 'Cancel Membership',
      contactForChangesTitle: 'Contact Us',
      contactForChangesMessage: 'To change or cancel your membership, please contact us directly.',
      confirmCancelMembership: 'Yes, Cancel',
      cancelMembershipTitle: 'Cancel Membership?',
      cancelMembershipMessage: 'Your current plan will stay in effect until the end of the term. You will not be charged again, and your access ends at term end.',
      changeScheduledTitle: 'Change Scheduled',
      changeScheduledMessage: 'Your current plan stays in effect until the end of the term. The new plan will take effect then.',
      changeProratedHint: 'Upgrades are billed at a prorated rate for the remainder of the current term.',
      payProrated: 'Pay Prorated Amount',
      scheduleChange: 'Schedule Change',
      upgrade: 'Upgrade',
      downgrade: 'Downgrade (scheduled)',
      pendingChange: 'Pending change',
      newMember: 'New Member',
      addToQueue: 'Add to Summary',
      pendingChangesTitle: 'Review Changes',
      pendingChangesSubtitle: 'Confirm to pay prorated upgrade fees. Downgrades are scheduled for end of term with no charge.',
      planTransition: '{{from}} → {{to}}',
      proratedAmount: 'Prorated: {{amount}}',
      totalProrated: 'Total Prorated Due',
      noUpgradesScheduled: 'No charge — all changes scheduled for end of term.',
      reviewAndPay: 'Review & Pay',
      confirmChanges: 'Confirm Changes',
      remove: 'Remove',
      scheduledAppliedTitle: 'Changes Scheduled',
      scheduledAppliedMessage: 'End-of-term changes have been scheduled. No charge is due.',

      // Scheduled banners
      scheduledChangeTitle: 'Switching to {{plan}} at end of term',
      scheduledCancelTitle: 'Cancellation scheduled',
      scheduledEndOfTerm: 'Current plan stays in effect until the end of the term.',
      undo: 'Undo',
    },

    // ─── Membership status ─────────────────────────────────────────────
    membershipStatus: {
      active: 'Active',
      expired: 'Expired',
      cancelled: 'Cancelled',
      paused: 'Paused',
    },

    // ─── Payment status ────────────────────────────────────────────────
    paymentStatus: {
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
      refunded: 'Refunded',
    },

    // ─── Billing cycle ─────────────────────────────────────────────────
    billingCycle: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      annual: 'Annual',
    },

    // ─── Legal ─────────────────────────────────────────────────────────
    legal: {
      gateTitle: 'Legal Agreements',
      disclaimer: 'Please review and sign the required documents to use the app and check in/participate in classes.',
      progressLabel: '{{count}} Document remaining',
      scrollHint: 'Read to the bottom to continue',
      loadingDocument: 'Loading document...',
      scrollToEndToEnable: 'Please read to the end of the document to enable the checkboxes below.',
      signingFormTitle: 'Signing Form',
      signedTitle: 'Signed ✓',
      loadingNextDoc: 'Loading next document…',
      expandForm: 'Expand form',
      collapseForm: 'Collapse form',
      signerNameLabel: 'Type your full legal name',
      agreeAndSign: 'Agree & Sign',
      signFailed: 'Failed to submit agreement',
      signedSuccess: 'Agreement signed successfully',
      subUserBlockedTitle: 'Account Access Pending',
      subUserBlockedBody: 'Your guardian must accept the guardian waiver before you can use the app. Please ask them to sign in and complete the required documents.',
      adultSubUserInviteTitle: 'Adult Dependent Account Setup',
      adultSubUserInviteBody: '{{name}} is 18 years or older and must sign their own legal documents and waivers. As a guardian, you cannot sign legal documents on behalf of an adult. Please invite {{name}} to set up their own account so they can sign in and complete their waivers.',
      adultSubUserStatusInvited: 'Status: Invitation sent to {{email}}',
      adultSubUserStatusPending: 'Status: Pending Setup',
      adultSubUserInstructionsInvited: 'Waiting for registration and waiver completion. They can scan the QR code below or use the invitation link to register and sign.',
      adultSubUserInstructionsPending: 'Please share the invitation link or QR code below to allow them to register and sign their own waivers.',
      profileSection: 'Legal Documents',
      viewPdf: 'View PDF',
      downloadCertificate: 'Download Certificate',
      reAgree: 'Re-Agree',
      agreed: 'Agreed',
      updateRequired: 'Update Required',
      docs: {
        athlete_waiver: { title: 'Athlete Waiver' },
        athlete_code_of_conduct: { title: 'Athlete Code of Conduct' },
        guardian_waiver: { title: 'Guardian Waiver' },
        guardian_code_of_conduct: { title: 'Guardian Code of Conduct' },
      },
      affirmations: {
        athlete: {
          read_and_understand: 'I have read and understand this document',
          legally_bound: 'I agree to be legally bound by this document',
          voluntary_assumption_of_risk: 'I acknowledge the voluntary assumption of risk',
          abide_by_rules: 'I agree to abide by the rules and conduct standards',
        },
        guardian: {
          confirm_guardianship: 'I confirm that I am the parent or legal guardian of the minor child named below',
          read_and_understand: 'I have read, understand, and agree to be legally bound by this document',
          voluntary_assumption_of_risk: 'I acknowledge the voluntary assumption of risk and release of liability on behalf of my child',
          data_consent: 'I consent to CBJJ Saigon collecting and processing my child\'s personal data (including name, DOB, and training progress) for membership and belt certification purposes as described in the [[PP_LINK]].',
          data_consent_over_7: 'My child and I have read and consent to the collection of personal data and training progress as described in the [[PP_LINK]].',
          abide_by_rules: 'I agree to abide by the rules and conduct standards on behalf of my child',
        },
      },
      minorNameLabel: "Minor Child's Full Legal Name",
      guardianNameLabel: "Guardian's Full Legal Name",
      minorNameRequired: 'Please enter the minor child\'s full legal name',
      loadError: 'Failed to load document. Please check your connection and try again.',
      allSignedTitle: "You're All Set! 🎉",
      allSignedMessage: 'All required documents have been signed. Your membership is active and you are ready to use the app and check in for classes.',
      allSignedButton: 'Continue to App',
    },

    // ─── Account Deletion ─────────────────────────────────────────────
    accountDeletion: {
      title: 'Delete Account',
      link: 'Delete Account',
      loggedOutIntro: 'You have the right to delete your CBJJ Saigon account and associated personal data. To proceed, you must first sign in to verify your identity.',
      loggedInIntro: 'You can permanently delete your CBJJ Saigon account and all associated personal data from this screen. This action is immediate and irreversible.',
      planLossWarning: 'Warning: Deleting your account will immediately terminate your membership plan. If you have time remaining on your current plan, no refund will be issued.',
      stepsTitle: 'How Account Deletion Works',
      loggedOutStep1: 'Sign in to your CBJJ Saigon account using the button below.',
      loggedOutStep2: 'Once signed in, return to this screen and follow the confirmation steps to delete your account.',
      loggedOutStep3: 'Your account and personal data will be permanently deleted immediately upon confirmation.',
      loggedInStep1: 'Review the data deletion and retention information above.',
      loggedInStep2: 'Tap "Delete Account" and confirm by typing your email address. Your account will be deleted immediately.',
      dataTitle: 'Data Deletion & Retention',
      dataDeleted: 'The following data will be permanently deleted: profile information (name, email, date of birth, weight, height), training progress and belt records, class check-in history, and family/guardian links.',
      subUsersDeleted: 'Dependent sub-users (children or family members who do not have their own login) linked to your account will also be deleted, along with their data.',
      subUsersRetained: 'Registered adult sub-users who have their own email login will NOT be deleted — they will simply be unlinked from your account and keep their own data.',
      dataRetained: 'The following data will be retained as required by law or for legitimate business purposes: payment records (with billing details), signed legal agreements, anonymized aggregate statistics, financial transaction records required by Vietnamese tax law, and legal agreement copies retained for the duration of the applicable statute of limitations.',
      retentionPeriod: 'Retained financial and legal records are kept for up to 7 years from the date of the transaction or agreement, after which they are permanently deleted.',
      signInButton: 'Sign In',
      requestButton: 'Delete Account',
      confirmWarning: 'This action will permanently delete your account immediately. You will lose access to your plan without a refund. To confirm, type your account email address below.',
      yourEmail: 'Your account email',
      emailConfirmLabel: 'Type your email to confirm',
      emailMismatch: 'The email you entered does not match your account email.',
      confirmButton: 'Delete My Account',
      requestFailed: 'Failed to delete account. Please try again later.',
      successTitle: 'Account Deleted',
      successBody: 'Your account has been permanently deleted. You have been signed out.',
      cancel: 'Cancel',
      goToLogin: 'Go to Login',
    },
  },
};

export default en;
